import { useCallback } from "react";
import useSWR from "swr";
import { FileInfo, Paginated, PaginationQuery } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { RenameSupplierDocument, SupplierDocument } from "./types";

export const useSupplierDocumentsApi = () => {
  const api = useApi();

  const useSupplierDocuments = useCallback(
    (query: PaginationQuery, signal: AbortSignal, supplierId?: string) => {
      const url = buildUrl(
        supplierId ? `/admin/suppliers/${supplierId}/documents` : "/admin/supplier_documents",
        query,
      );
      return useSWR<Paginated<SupplierDocument>, CustomError, string>(url, (url) =>
        api.get<Paginated<SupplierDocument>>({ url, signal }),
      );
    },
    [api],
  );

  const deleteSupplierDocument = useCallback(
    async (documentId: string, supplierId?: string): Promise<void> => {
      const url = `/admin/supplier_documents/${documentId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/supplier_documents`, `/admin/suppliers/${supplierId}/documents`]);
      });
    },
    [api],
  );

  const insertSupplierDocument = useCallback(
    async (file: File, overWrite?: boolean, supplierId?: string): Promise<void> => {
      const formData = new FormData();
      formData.append("file", file, file.name);

      await api.postNoResult<FormData>(
        `/admin/suppliers/${supplierId}/documents${overWrite ? "?overwrite=1" : ""}`,
        formData,
      );
      api.mutateCache([`/admin/suppliers/${supplierId}/documents`, "/admin/supplier_documents"]);
    },
    [api],
  );

  const getSupplierDocument = useCallback(
    (docID: string, signal: AbortSignal) => {
      const url = `/admin/supplier_documents/${docID}`;
      const r = api.get<FileInfo>({ url, signal });
      return r;
    },
    [api],
  );

  const renameDocument = useCallback(
    async (docId: string, body: RenameSupplierDocument, supplierId?: string): Promise<void> => {
      await api.put<RenameSupplierDocument>(`/admin/supplier_documents/${docId}`, body);
      api.mutateCache([`/admin/suppliers/${supplierId}/documents`, "/admin/supplier_documents"]);
    },
    [api],
  );

  return {
    useSupplierDocuments,
    deleteSupplierDocument,
    insertSupplierDocument,
    getSupplierDocument,
    renameDocument,
  };
};
